import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  ArrowLeft,
  Building2,
  MapPin,
  Users,
  IndianRupee,
  Calendar,
  Globe,
  FileText,
  Download,
  Sparkles,
  Clock,
  CheckCircle,
  TrendingUp,
  Target,
  Rocket,
  Scale,
  Package,
  Code,
  Award,
  Briefcase,
  Eye,
  Star,
  X,
  CalendarDays,
  MessageSquare,
  Share2,
  AlertTriangle,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { ScrollArea } from '@/components/ui/scroll-area'
import { startupsApi, analysisApi, startupStatusApi } from '@/services/api'
import { DocumentPreviewModal } from '@/components/document-preview-modal'
import { formatDate, formatMonthYear, formatFileSize, capitalizeFirst, cn, getStartupData } from '@/utils'
import { useToast } from '@/hooks/use-toast'
import { useAuth } from '@/hooks/use-auth'

// Custom styles mapping to user request
const displayFont = { fontFamily: '"Inter", sans-serif' }
const headingFont = { fontFamily: '"Outfit", sans-serif' }

// Q&A Components
import { AskQuestionModal } from '@/components/questions/ask-question-modal'
import { AnswerQuestionsModal } from '@/components/questions/answer-questions-modal'
import { QuestionsList } from '@/components/questions/questions-list'
import { ActivityFeed } from '@/components/activity/activity-feed'
import { useStartupQuestions } from '@/hooks/use-questions'

// Helper functions for Indian market formatting
const formatINR = (amount: number | undefined) => {
  if (!amount) return 'Not specified'
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount)
}

const formatCrores = (amount: number | undefined) => {
  if (!amount) return 'Not specified'
  const crores = amount / 10000000
  return `₹${crores.toFixed(2)} Cr`
}

const formatPercent = (value: number | undefined) => {
  if (!value) return 'Not specified'
  return `${value.toFixed(1)}%`
}

export const StartupDetailPage = () => {

  const { id } = useParams<{ id: string }>()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [noteText, setNoteText] = useState('')
  const [isEditingNote, setIsEditingNote] = useState(false)

  // Get current user and role
  const { user, isInvestor, isFounder } = useAuth()

  const [questionSort, setQuestionSort] = useState<'newest' | 'oldest'>('newest')
  const [questionFilter, setQuestionFilter] = useState<'all' | 'answered' | 'pending'>('all')

  // Document preview state
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewGcsPath, setPreviewGcsPath] = useState('')
  const [previewFilename, setPreviewFilename] = useState('')

  const { data: startup, isLoading } = useQuery({
    queryKey: ['startup', id],
    queryFn: () => {
      if (id === 'my-startup') {
        return startupsApi.getMyStartup() as Promise<any> // Type cast if needed, or update return type
      }
      return startupsApi.get(id!)
    },
    enabled: !!id,
  })

  const { data: analyses } = useQuery({
    queryKey: ['analyses', id, user?.uid],
    queryFn: () => analysisApi.list({ startup_id: id }),
    enabled: !!id && !isFounder && !!user?.uid,
  })

  // Get startup status (investors only)
  const { data: startupStatus, refetch: refetchStatus, isLoading: isStatusLoading, isError: isStatusError, error: statusError } = useQuery({
    queryKey: ['startup-status', id],
    queryFn: () => startupStatusApi.getStatus(id!),
    enabled: !!id && !isFounder,
  })

  // Get questions for this startup
  const { data: allQuestions = [] } = useStartupQuestions(id || '', undefined)

  // Filter and sort questions
  const filteredQuestions = allQuestions.filter(q => {
    if (questionFilter === 'all') return true
    return q.status === questionFilter
  }).sort((a, b) => {
    const dateA = new Date(a.created_at).getTime()
    const dateB = new Date(b.created_at).getTime()
    return questionSort === 'newest' ? dateB - dateA : dateA - dateB
  })

  const pendingQuestions = allQuestions.filter(q => q.status === 'pending')
  const answeredQuestions = allQuestions.filter(q => q.status === 'answered')

  const latestAnalysis = analyses?.data?.[0]
  const hasAnalysis = !!latestAnalysis

  // Normalize status for comparison
  const analysisStatus = latestAnalysis?.status?.toLowerCase()

  const isAnalysisInProgress = hasAnalysis && ['in_progress', 'running', 'processing', 'pending'].includes(analysisStatus)
  const isAnalysisCompleted = hasAnalysis && analysisStatus === 'completed'

  // Status update mutation
  const updateStatusMutation = useMutation({
    mutationFn: ({ status }: { status: string }) => startupStatusApi.updateStatus(id!, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['startupStatus', id] })
      toast({
        title: 'Status Updated',
        description: 'Startup status has been updated successfully',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive',
      })
    },
  })

  // Note update mutation
  const updateNoteMutation = useMutation({
    mutationFn: ({ note }: { note: string }) => startupStatusApi.updateNote(id!, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['startupStatus', id] })
      setIsEditingNote(false)
      toast({
        title: 'Note Saved',
        description: 'Your note has been saved successfully',
      })
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to save note',
        variant: 'destructive',
      })
    },
  })

  const handleStatusUpdate = (status: string) => {
    updateStatusMutation.mutate({ status })
  }

  const handleScheduleMeeting = () => {
    const founderEmail = startup?.profile?.company_info?.founders?.[0]?.email
    if (founderEmail) {
      window.location.href = `mailto:${founderEmail}?subject=Meeting Request - ${startup?.profile?.company_info?.name}`
    }
  }

  const handleShare = () => {
    const url = window.location.href
    navigator.clipboard.writeText(url)
    toast({
      title: 'Link Copied!',
      description: 'Startup page link copied to clipboard',
    })
  }

  const handleSaveNote = () => {
    if (noteText.trim()) {
      updateNoteMutation.mutate({ note: noteText })
    }
  }

  const currentStatus = startupStatus?.status || 'New'

  // Analysis status component (non-founders only)
  const AnalysisStatusCard = () => {
    if (isFounder) return null
    if (isAnalysisCompleted) {
      return (
        <Card className="glass-card overflow-hidden shadow-xl border-white/50 dark:border-slate-700/50">
          <CardContent className="p-4 relative">
            <div className="absolute inset-0 bg-logo-gradient opacity-10"></div>
            <div className="flex flex-col sm:flex-row items-center sm:items-center justify-between gap-4 relative z-10">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-12 h-12 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center shadow-lg">
                    <Sparkles className="w-6 h-6 text-[#A378CF] animate-pulse" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-logo-gradient rounded-full flex items-center justify-center">
                    <CheckCircle className="w-3 h-3 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-display font-black logo-gradient-text mb-0.5" style={headingFont}>Analysis Ready! ✨</h3>
                  <p className="text-slate-600 dark:text-slate-400 text-xs">Deep insights available for this startup</p>
                </div>
              </div>
              <Button size="sm" className="w-full sm:w-auto bg-logo-gradient text-white font-bold h-9 rounded-lg transition-all shadow-md hover:opacity-90 border-none px-4" asChild>
                <Link to={`/startups/${id}/analysis`}>
                  <Sparkles className="w-4 h-4 mr-2" />
                  <span>View Analysis</span>
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )
    }

    if (isAnalysisInProgress) {
      return (
        <Card className="glass-card shadow-lg border-blue-500/20">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-blue-50 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
                <Clock className="w-8 h-8 text-[#4194F2] animate-spin" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg md:text-xl font-display font-bold text-slate-900 dark:text-white mb-1" style={headingFont}>Analysis in Progress</h3>
                <p className="text-slate-600 dark:text-slate-400">Our AI agents are analyzing this startup...</p>
                <div className="flex items-center gap-3 mt-3">
                  <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-logo-gradient rounded-full animate-pulse" style={{ width: '60%' }}></div>
                  </div>
                  <span className="text-xs font-bold text-[#4f46e5]">60%</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )
    }

    return null
  }

  const InfoRow = ({ icon: Icon, label, value }: { icon: any, label: string, value: string | undefined }) => {
    if (!value || value === 'Not specified') return null
    return (
      <div className="flex items-start gap-4 p-4 rounded-2xl bg-white/50 dark:bg-slate-800/50 border border-slate-200/50 dark:border-slate-700/50 shadow-sm hover:shadow-md transition-all group">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center shrink-0 group-hover:bg-indigo-100 dark:group-hover:bg-indigo-900/40 transition-colors">
          <Icon className="w-5 h-5 text-[#4194F2]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-[10px] uppercase tracking-wider text-slate-400 mb-1">{label}</p>
          <p className="text-sm md:text-base font-bold text-slate-900 dark:text-white break-words">{value}</p>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-64 bg-muted rounded"></div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="h-48 bg-muted rounded"></div>
            <div className="h-48 bg-muted rounded"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!startup) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold mb-2">Startup not found</h3>
          <p className="text-muted-foreground mb-4">
            The startup you're looking for doesn't exist or you don't have access to it.
          </p>
          <Button asChild>
            <Link to="/startups">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Startups
            </Link>
          </Button>
        </div>
      </div>
    )
  }

  const { profile } = startup
  const { company_info = {} as any, documents = [] } = profile || {}
  const { founders = [], metadata = {} as any } = company_info

  return (
    <div className="bg-white dark:bg-background-dark text-slate-900 dark:text-white overflow-x-hidden antialiased transition-colors duration-300 min-h-screen relative flex flex-col" style={displayFont}>
      <style>{`
        .material-symbols-outlined { font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24; }
        .filled-icon { font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24; }
      `}</style>
      <div className="p-4 md:p-6 space-y-6 relative z-10">
        {/* Header */}
        {/* Profile Completion Warning (Founders only) */}
        {isFounder && (!company_info.description || !company_info.logo_url || !founders.length) && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4 animate-in fade-in slide-in-from-top-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 dark:text-white mb-0.5">Profile Incomplete</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">Complete your startup profile to increase visibility and attract investors.</p>
              </div>
            </div>
            <Button asChild className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-white font-bold border-none shadow-md shadow-amber-500/20">
              <Link to={`/startups/${id}/edit`}>
                Complete Profile
              </Link>
            </Button>
          </div>
        )}

        {/* Modern Gradient Header Card */}
        <div className="rounded-3xl overflow-hidden glass-card shadow-lg border border-white/50 dark:border-slate-700/50 p-4 md:p-5 relative">
          <div className="absolute inset-0 bg-logo-gradient opacity-100"></div>

          <div className="relative z-10">
            <div className="flex flex-col lg:flex-row items-center lg:items-start justify-between gap-4 md:gap-6">
              {/* Left - Back Button + Company Details */}
              <div className="flex flex-col md:flex-row items-center md:items-start gap-4 flex-1 w-full">
                {/* Back Button */}
                <Button
                  variant="ghost"
                  size="icon"
                  asChild
                  className="text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full mb-2 md:mb-0 h-8 w-8"
                >
                  <Link to="/startups">
                    <ArrowLeft className="w-4 h-4" />
                  </Link>
                </Button>

                {company_info.logo_url ? (
                  <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl bg-white p-1.5 shadow-xl border border-white/50 flex-shrink-0 flex items-center justify-center overflow-hidden">
                    <img
                      src={company_info.logo_url}
                      alt={`${company_info.name} logo`}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-logo-gradient rounded-xl flex items-center justify-center shadow-xl border border-white/20 flex-shrink-0">
                    <Building2 className="w-6 h-6 md:w-8 md:h-8 text-white" />
                  </div>
                )}
                <div className="flex-1 text-center md:text-left">
                  <div className="flex flex-col md:flex-row items-center gap-2 md:gap-3 mb-1.5">
                    <h1 className="text-2xl md:text-3xl lg:text-4xl font-display font-black text-white dark:text-slate-900 tracking-tight" style={headingFont}>
                      {company_info.name}
                    </h1>
                    {/* Edit Button for Founders */}
                    {isFounder && (
                      <Button
                        size="sm"
                        variant="secondary"
                        asChild
                        className="bg-white/20 hover:bg-white/30 text-white border-none rounded-full px-4 h-8"
                      >
                        <Link to={`/startups/${startup.id}/edit`}>
                          <span className="material-symbols-outlined text-lg mr-1">edit</span>
                          Edit
                        </Link>
                      </Button>
                    )}
                  </div>
                  <div className="flex items-center justify-center md:justify-start gap-2 text-slate-600 dark:text-slate-400 text-xs md:text-sm font-bold flex-wrap">
                    <span className="flex items-center gap-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-1.5 rounded-full shadow-sm">
                      <span className="w-2 h-2 bg-[#4194F2] rounded-full" />
                      {capitalizeFirst(company_info.industry?.replace('_', ' ') || 'N/A')}
                    </span>
                    <span className="flex items-center gap-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-1.5 rounded-full shadow-sm">
                      <span className="w-2 h-2 bg-[#A378CF] rounded-full" />
                      {capitalizeFirst(company_info.funding_stage?.replace('_', ' ') || 'N/A')}
                    </span>
                    <span className="flex items-center gap-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-1.5 rounded-full shadow-sm">
                      <span className="w-2 h-2 bg-[#F0766E] rounded-full" />
                      {capitalizeFirst(company_info.product_stage?.replace('_', ' ') || 'N/A')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right - Status & Action Buttons (Non-founders only) */}
              {!isFounder && (
                <div className="flex items-center justify-center lg:justify-end gap-2 flex-wrap w-full lg:w-auto">
                  {/* Current Status Badge */}
                  <div className="flex items-center gap-2 bg-white dark:bg-slate-800 rounded-xl px-3 py-2 border border-slate-200 dark:border-slate-700 shadow-lg">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</span>
                    <Badge className="text-xs font-black px-3 py-1 bg-logo-gradient text-white border-none shadow-md">
                      {currentStatus}
                    </Badge>
                  </div>

                  <div className="w-full h-px lg:w-px lg:h-8 bg-slate-200 dark:bg-slate-700 my-1 lg:my-0 lg:mx-1" />

                  <div className="flex items-center gap-1.5 flex-wrap justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusUpdate('On Watch')}
                      disabled={updateStatusMutation.isPending}
                      className={`h-8 px-3 rounded-lg text-xs border-slate-200 dark:border-slate-700 font-bold transition-all hover:bg-slate-50 dark:hover:bg-slate-800 ${currentStatus === 'On Watch' ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-500' : ''}`}
                    >
                      <Eye className="w-3.5 h-3.5 mr-1.5 text-[#4194F2]" />
                      Watch
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusUpdate('Shortlist')}
                      disabled={updateStatusMutation.isPending}
                      className={`h-8 px-3 rounded-lg text-xs border-slate-200 dark:border-slate-700 font-bold transition-all hover:bg-slate-50 dark:hover:bg-slate-800 ${currentStatus === 'Shortlist' ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-50 border-indigo-500' : ''}`}
                    >
                      <Star className="w-3.5 h-3.5 mr-1.5 text-[#A378CF]" />
                      Shortlist
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleStatusUpdate('Pass')}
                      disabled={updateStatusMutation.isPending}
                      className={`h-8 px-3 rounded-lg text-xs border-slate-200 dark:border-slate-700 font-bold transition-all hover:bg-slate-50 dark:hover:bg-slate-800 ${currentStatus === 'Pass' ? 'bg-red-50 dark:bg-red-900/20 border-red-500' : ''}`}
                    >
                      <X className="w-3.5 h-3.5 mr-1.5 text-[#F0766E]" />
                      Pass
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleScheduleMeeting}
                      className="h-8 px-3 rounded-lg text-xs border-slate-200 dark:border-slate-700 font-bold transition-all hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      <CalendarDays className="w-3.5 h-3.5 mr-1.5 text-indigo-500" />
                      Meeting
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleShare}
                      className="h-8 px-3 rounded-lg text-xs border-slate-200 dark:border-slate-700 font-bold transition-all hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      <Share2 className="w-3.5 h-3.5 mr-1.5 text-slate-500" />
                      Share
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      asChild
                      className="h-8 px-3 rounded-lg text-xs border-slate-200 dark:border-slate-700 font-bold transition-all hover:bg-slate-50 dark:hover:bg-slate-800"
                    >
                      <Link to={`/startups/${id}/cheat-sheet`}>
                        <FileText className="w-3.5 h-3.5 mr-1.5 text-slate-500" />
                        Cheat Sheet
                      </Link>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Analysis Status Card */}
        <AnalysisStatusCard />

        {/* Notes Section (Non-founders only) */}
        {!isFounder && (
          <Card className="glass-card shadow-lg border-white/50 dark:border-slate-700/50 overflow-hidden">
            <div className="relative p-4 flex items-center gap-3 overflow-hidden">
              <div className="absolute inset-0 bg-logo-gradient opacity-10" />
              <span className="material-symbols-outlined text-slate-800 dark:text-slate-200 relative z-10">note_alt</span>
              <h3 className="font-bold text-slate-800 dark:text-slate-200 relative z-10" style={headingFont}>Investor Notes</h3>
            </div>
            <CardContent className="p-6">
              {!isEditingNote && !startupStatus?.investor_note ? (
                <button
                  onClick={() => {
                    setIsEditingNote(true)
                    setNoteText('')
                  }}
                  className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 text-slate-400 hover:text-[#4f46e5] hover:border-[#4f46e5] hover:bg-indigo-50/50 dark:hover:bg-indigo-900/10 transition-all font-bold text-sm"
                >
                  <span className="material-symbols-outlined">add_circle</span>
                  Add a private note about this startup...
                </button>
              ) : !isEditingNote && startupStatus?.investor_note ? (
                <div className="space-y-4">
                  <div className="p-5 bg-white/50 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm">
                    <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed">{startupStatus.investor_note}</p>
                  </div>
                  <div className="flex items-center justify-between px-2">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                      Last updated: {startupStatus.note_updated_at ? formatDate(startupStatus.note_updated_at) : 'N/A'}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsEditingNote(true)
                        setNoteText(startupStatus.investor_note || '')
                      }}
                      className="text-[#4f46e5] font-bold hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
                    >
                      Edit Note
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <Textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Enter your investment notes, concerns, or next steps..."
                    rows={4}
                    className="w-full rounded-2xl border-slate-200 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 text-slate-900 dark:text-white p-4 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] transition-all resize-none shadow-sm"
                  />
                  <div className="flex gap-3 justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setIsEditingNote(false)
                        setNoteText('')
                      }}
                      className="font-bold text-slate-500"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveNote}
                      disabled={updateNoteMutation.isPending || !noteText.trim()}
                      className="btn-logo-gradient-80 text-slate-800 dark:text-slate-200 font-bold px-6 rounded-xl shadow-lg"
                    >
                      <span>Save Note</span>
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Main Content Layout: Tabs on left, Activity Feed on right */}
        <div className="flex flex-col lg:flex-row gap-6 items-start">
          {/* Left side - Tabs */}
          <div className="flex-1 w-full overflow-hidden">
            <Card className="glass-card shadow-xl border-white/50 dark:border-slate-700/50">
              <CardContent className="p-0">
                <Tabs defaultValue="overview" className="space-y-0">
                  <div className="bg-white/50 dark:bg-slate-900/50 p-4 rounded-t-[1.5rem] border-b border-slate-200 dark:border-slate-800 overflow-x-auto scrollbar-hide">
                    <TabsList className="flex min-w-max md:grid md:w-full md:grid-cols-9 gap-2 bg-transparent p-0 h-auto">
                      {[
                        { value: 'overview', label: 'Overview' },
                        { value: 'product', label: 'Product' },
                        { value: 'market', label: 'Market' },
                        { value: 'traction', label: 'Traction' },
                        { value: 'financial', label: 'Financial' },
                        { value: 'team', label: 'Team' },
                        { value: 'legal', label: 'Legal' },
                        { value: 'vision', label: 'Vision' },
                        { value: 'documents', label: 'Documents', short: 'Docs' }
                      ].map((tab) => (
                        <TabsTrigger
                          key={tab.value}
                          value={tab.value}
                          className="tab-active-muted font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all rounded-xl py-2.5 md:py-3 px-4 text-xs md:text-sm border-none"
                        >
                          <span className="flex items-center gap-1.5">
                            <span className={tab.short ? "hidden xs:inline" : ""}>{tab.label}</span>
                            {tab.short && <span className="xs:hidden">{tab.short}</span>}
                          </span>
                        </TabsTrigger>
                      ))}
                    </TabsList>
                  </div>

                  {/* OVERVIEW TAB */}
                  <TabsContent value="overview" className="p-4">
                    <ScrollArea className="h-[480px] pr-4">
                      <div className="space-y-6">
                        <Card className="glass-card shadow-lg border-white/50 dark:border-slate-700/50 overflow-hidden">
                          <div className="relative p-4 flex items-center gap-3 overflow-hidden">
                            <div className="absolute inset-0 bg-logo-gradient opacity-10" />
                            <Building2 className="w-5 h-5 text-slate-800 dark:text-slate-200 relative z-10" />
                            <h3 className="font-bold text-slate-800 dark:text-slate-200 relative z-10" style={headingFont}>Company Overview</h3>
                          </div>
                          <CardContent className="pt-6 space-y-6">
                            <div>
                              <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-3">Description</h3>
                              <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-justify bg-white/30 dark:bg-slate-900/30 p-4 rounded-2xl border border-slate-200/50 dark:border-slate-700/50">
                                {company_info.description}
                              </p>
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2">
                              <InfoRow icon={MapPin} label="Location" value={company_info.location} />
                              <InfoRow icon={Users} label="Team Size" value={company_info.employee_count ? `${company_info.employee_count} employees` : undefined} />
                              <InfoRow icon={Globe} label="Website" value={company_info.website_url} />
                              <InfoRow icon={Calendar} label="Founded" value={company_info.founded_date ? formatMonthYear(company_info.founded_date) : undefined} />
                              <InfoRow icon={IndianRupee} label="Funding Raised" value={company_info.funding_raised ? formatINR(company_info.funding_raised) : undefined} />
                              <InfoRow icon={IndianRupee} label="Funding Seeking" value={company_info.funding_seeking ? formatINR(company_info.funding_seeking) : undefined} />
                              <InfoRow icon={Target} label="Problem" value={company_info.problem_statement} />
                              <InfoRow icon={Rocket} label="Solution" value={company_info.solution} />
                            </div>

                            {company_info.video_pitch_url && (
                              <div className="mt-6">
                                <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-3">Video Pitch</h3>
                                <div className="aspect-video rounded-2xl overflow-hidden shadow-lg border border-slate-200 dark:border-slate-700 bg-black">
                                  <video
                                    src={company_info.video_pitch_url}
                                    controls
                                    className="w-full h-full object-contain"
                                  />
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </ScrollArea>
                  </TabsContent>

                  {/* PRODUCT TAB */}
                  <TabsContent value="product" className="p-4">
                    <ScrollArea className="h-[480px] pr-4">
                      <div className="space-y-6">
                        <Card className="glass-card shadow-lg border-white/50 dark:border-slate-700/50 overflow-hidden">
                          <div className="relative p-4 flex items-center gap-3 overflow-hidden">
                            <div className="absolute inset-0 bg-logo-gradient opacity-10" />
                            <Package className="w-5 h-5 text-slate-800 dark:text-slate-200 relative z-10" />
                            <h3 className="font-bold text-slate-800 dark:text-slate-200 relative z-10" style={headingFont}>Product & Technology</h3>
                          </div>
                          <CardContent className="pt-6 space-y-6">
                            <div className="grid gap-4 md:grid-cols-2">
                              <InfoRow icon={Rocket} label="Product Stage" value={company_info.product_stage ? capitalizeFirst(company_info.product_stage) : undefined} />
                              <InfoRow icon={Code} label="Technology Stack" value={company_info.technology_stack} />
                            </div>

                            {company_info.ip_patents && (
                              <div className="p-5 bg-white/30 dark:bg-slate-900/30 rounded-2xl border border-slate-200/50 dark:border-slate-700/50">
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                  <Award className="w-4 h-4 text-[#A378CF]" />
                                  IP & Patents
                                </h3>
                                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap text-justify leading-relaxed">{company_info.ip_patents}</p>
                              </div>
                            )}

                            {company_info.development_timeline && (
                              <div className="p-5 bg-white/30 dark:bg-slate-900/30 rounded-2xl border border-slate-200/50 dark:border-slate-700/50">
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Development Timeline</h3>
                                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap text-justify leading-relaxed">{company_info.development_timeline}</p>
                              </div>
                            )}

                            {company_info.product_roadmap && (
                              <div className="p-5 bg-white/30 dark:bg-slate-900/30 rounded-2xl border border-slate-200/50 dark:border-slate-700/50">
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Product Roadmap</h3>
                                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap text-justify leading-relaxed">{company_info.product_roadmap}</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </ScrollArea>
                  </TabsContent>

                  {/* MARKET TAB */}
                  <TabsContent value="market" className="p-4">
                    <ScrollArea className="h-[480px] pr-4">
                      <div className="space-y-6">
                        <Card className="glass-card shadow-lg border-white/50 dark:border-slate-700/50 overflow-hidden">
                          <div className="relative p-4 flex items-center gap-3 overflow-hidden">
                            <div className="absolute inset-0 bg-logo-gradient opacity-10" />
                            <Target className="w-5 h-5 text-slate-800 dark:text-slate-200 relative z-10" />
                            <h3 className="font-bold text-slate-800 dark:text-slate-200 relative z-10" style={headingFont}>Market & Customers</h3>
                          </div>
                          <CardContent className="pt-6 space-y-6">
                            {company_info.target_customer_profile && (
                              <div className="p-5 bg-white/30 dark:bg-slate-900/30 rounded-2xl border border-slate-200/50 dark:border-slate-700/50">
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Target Customer Profile</h3>
                                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap text-justify leading-relaxed">{company_info.target_customer_profile}</p>
                              </div>
                            )}

                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                              <InfoRow icon={IndianRupee} label="CAC" value={formatINR(company_info.customer_acquisition_cost)} />
                              <InfoRow icon={IndianRupee} label="LTV" value={formatINR(company_info.lifetime_value)} />
                              <InfoRow icon={Users} label="Current Customers" value={company_info.current_customer_count?.toString()} />
                              <InfoRow icon={TrendingUp} label="Retention Rate" value={formatPercent(company_info.customer_retention_rate)} />
                              <InfoRow icon={MapPin} label="Geographic Markets" value={company_info.geographic_markets} />
                            </div>

                            {company_info.go_to_market_strategy && (
                              <div className="p-5 bg-white/30 dark:bg-slate-900/30 rounded-2xl border border-slate-200/50 dark:border-slate-700/50">
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Go-to-Market Strategy</h3>
                                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap text-justify leading-relaxed">{company_info.go_to_market_strategy}</p>
                              </div>
                            )}

                            <div className="grid gap-4 sm:grid-cols-3">
                              <div className="p-5 rounded-2xl bg-[#3B52D0]/5 dark:bg-[#3B52D0]/20 border border-[#3B52D0]/20 shadow-sm relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:scale-110 transition-transform">
                                  <span className="material-symbols-outlined text-4xl text-[#3B52D0]">groups</span>
                                </div>
                                <p className="text-[10px] font-black text-[#3B52D0] uppercase tracking-widest mb-1">TAM</p>
                                <p className="text-2xl font-black text-slate-900 dark:text-white" style={headingFont}>{formatCrores(metadata?.market_size_tam)}</p>
                              </div>
                              <div className="p-5 rounded-2xl bg-[#4194F2]/5 dark:bg-[#4194F2]/20 border border-[#4194F2]/20 shadow-sm relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:scale-110 transition-transform">
                                  <span className="material-symbols-outlined text-4xl text-[#4194F2]">target</span>
                                </div>
                                <p className="text-[10px] font-black text-[#4194F2] uppercase tracking-widest mb-1">SAM</p>
                                <p className="text-2xl font-black text-slate-900 dark:text-white" style={headingFont}>{formatCrores(metadata?.market_size_sam)}</p>
                              </div>
                              <div className="p-5 rounded-2xl bg-[#A378CF]/5 dark:bg-[#A378CF]/20 border border-[#A378CF]/20 shadow-sm relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-2 opacity-10 group-hover:scale-110 transition-transform">
                                  <span className="material-symbols-outlined text-4xl text-[#A378CF]">ads_click</span>
                                </div>
                                <p className="text-[10px] font-black text-[#A378CF] uppercase tracking-widest mb-1">SOM</p>
                                <p className="text-2xl font-black text-slate-900 dark:text-white" style={headingFont}>{formatCrores(metadata?.market_size_som)}</p>
                              </div>
                            </div>

                            {metadata?.main_competitors && metadata.main_competitors.length > 0 && (
                              <div className="p-5 bg-white/30 dark:bg-slate-900/30 rounded-2xl border border-slate-200/50 dark:border-slate-700/50">
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Main Competitors</h3>
                                <ul className="space-y-1">
                                  {metadata.main_competitors.map((competitor, i) => (
                                    <li key={i} className="text-slate-700 dark:text-slate-300 leading-relaxed">• {competitor}</li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {metadata?.unique_value_proposition && (
                              <div className="p-5 bg-white/30 dark:bg-slate-900/30 rounded-2xl border border-slate-200/50 dark:border-slate-700/50">
                                <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-white opacity-10 rounded-full blur-3xl"></div>
                                <h3 className="text-[10px] font-black uppercase tracking-widest mb-3 text-white/80">Unique Value Proposition</h3>
                                <p className="text-base font-bold italic leading-relaxed">"{metadata.unique_value_proposition}"</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </ScrollArea>
                  </TabsContent>

                  {/* TRACTION TAB */}
                  <TabsContent value="traction" className="p-4">
                    <ScrollArea className="h-[480px] pr-4">
                      <div className="space-y-6">
                        <Card className="glass-card shadow-lg border-white/50 dark:border-slate-700/50 overflow-hidden">
                          <div className="relative p-4 flex items-center gap-3 overflow-hidden">
                            <div className="absolute inset-0 bg-logo-gradient opacity-10" />
                            <TrendingUp className="w-5 h-5 text-slate-800 dark:text-slate-200 relative z-10" />
                            <h3 className="font-bold text-slate-800 dark:text-slate-200 relative z-10" style={headingFont}>Traction & Metrics</h3>
                          </div>
                          <CardContent className="pt-6 space-y-6">
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                              <div className="p-5 rounded-2xl bg-white/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 shadow-sm relative overflow-hidden group">
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">MRR</p>
                                <p className="text-xl font-black text-[#3B52D0] dark:text-[#4194F2]" style={headingFont}>{formatINR(company_info.monthly_recurring_revenue)}</p>
                              </div>
                              <div className="p-5 rounded-2xl bg-white/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 shadow-sm relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-2 opacity-5 group-hover:scale-110 transition-transform">
                                  <span className="material-symbols-outlined text-4xl text-[#A378CF]">calendar_month</span>
                                </div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">ARR</p>
                                <p className="text-xl font-black text-[#A378CF]" style={headingFont}>{formatINR(company_info.annual_recurring_revenue)}</p>
                              </div>
                              <div className="p-5 rounded-2xl bg-white/50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 shadow-sm relative overflow-hidden group">
                                <div className="absolute top-0 right-0 p-2 opacity-5 group-hover:scale-110 transition-transform">
                                  <span className="material-symbols-outlined text-4xl text-[#F0766E]">speed</span>
                                </div>
                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Run Rate</p>
                                <p className="text-xl font-black text-[#F0766E]" style={headingFont}>{formatINR(company_info.revenue_run_rate)}</p>
                              </div>
                            </div>

                            <div className="grid gap-4 sm:grid-cols-2">
                              <InfoRow icon={TrendingUp} label="Revenue Growth" value={formatPercent(company_info.revenue_growth_rate)} />
                              <InfoRow icon={TrendingUp} label="User Growth" value={formatPercent(company_info.user_growth_rate)} />
                              <InfoRow icon={IndianRupee} label="Burn Rate" value={company_info.burn_rate ? `${formatINR(company_info.burn_rate)}/month` : undefined} />
                              <InfoRow icon={Calendar} label="Runway" value={company_info.runway_months ? `${company_info.runway_months} months` : undefined} />
                            </div>

                            {company_info.key_performance_indicators && (
                              <div className="p-5 bg-white/30 dark:bg-slate-900/30 rounded-2xl border border-slate-200/50 dark:border-slate-700/50">
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Key Performance Indicators</h3>
                                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap text-justify leading-relaxed">{company_info.key_performance_indicators}</p>
                              </div>
                            )}

                            {metadata?.traction_highlights && metadata.traction_highlights.length > 0 && (
                              <div className="p-5 bg-[#4194F2]/5 dark:bg-[#4194F2]/20 rounded-2xl border border-[#4194F2]/20">
                                <h3 className="text-[10px] font-black text-[#4194F2] uppercase tracking-widest mb-3">Traction Highlights</h3>
                                <div className="grid gap-3">
                                  {metadata.traction_highlights.map((highlight, i) => (
                                    <div key={i} className="flex items-start gap-3 bg-white/50 dark:bg-slate-800/50 p-3 rounded-xl border border-[#4194F2]/10 group transition-all hover:translate-x-1">
                                      <div className="w-6 h-6 rounded-full bg-[#4194F2]/10 flex items-center justify-center shrink-0 mt-0.5">
                                        <span className="material-symbols-outlined text-[14px] text-[#4194F2]">auto_awesome</span>
                                      </div>
                                      <span className="text-slate-700 dark:text-slate-300 text-sm font-medium">{highlight}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </ScrollArea>
                  </TabsContent>

                  {/* FINANCIAL TAB */}
                  <TabsContent value="financial" className="p-4">
                    <ScrollArea className="h-[480px] pr-4">
                      <div className="space-y-6">
                        <Card className="glass-card shadow-lg border-white/50 dark:border-slate-700/50 overflow-hidden">
                          <div className="relative p-4 flex items-center gap-3 overflow-hidden">
                            <div className="absolute inset-0 bg-logo-gradient opacity-10" />
                            <IndianRupee className="w-5 h-5 text-slate-800 dark:text-slate-200 relative z-10" />
                            <h3 className="font-bold text-slate-800 dark:text-slate-200 relative z-10" style={headingFont}>Financial Details</h3>
                          </div>
                          <CardContent className="pt-6 space-y-6">
                            <div className="grid gap-4 md:grid-cols-2">
                              <InfoRow icon={IndianRupee} label="Funding Raised" value={formatINR(company_info.funding_raised)} />
                              <InfoRow icon={IndianRupee} label="Funding Seeking" value={formatINR(company_info.funding_seeking)} />
                            </div>

                            {company_info.previous_funding_rounds && (
                              <div className="p-5 bg-white/30 dark:bg-slate-900/30 rounded-2xl border border-slate-200/50 dark:border-slate-700/50">
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Previous Funding Rounds</h3>
                                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap text-justify leading-relaxed">{company_info.previous_funding_rounds}</p>
                              </div>
                            )}

                            {company_info.current_investors && (
                              <div className="p-5 bg-white/30 dark:bg-slate-900/30 rounded-2xl border border-slate-200/50 dark:border-slate-700/50">
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Current Investors</h3>
                                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap text-justify leading-relaxed">{company_info.current_investors}</p>
                              </div>
                            )}

                            {company_info.use_of_funds && (
                              <div className="p-5 bg-[#4194F2]/5 dark:bg-[#4194F2]/20 rounded-2xl border border-[#4194F2]/20">
                                <h3 className="text-[10px] font-black text-[#4194F2] uppercase tracking-widest mb-3">Use of Funds</h3>
                                <p className="text-slate-700 dark:text-slate-300 whitespace-pre-wrap text-justify leading-relaxed">{company_info.use_of_funds}</p>
                              </div>
                            )}

                            <div className="grid gap-4 md:grid-cols-2">
                              <InfoRow icon={Calendar} label="Profitability Timeline" value={company_info.profitability_timeline} />
                              <InfoRow icon={TrendingUp} label="Unit Economics" value={company_info.unit_economics} />
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </ScrollArea>
                  </TabsContent>

                  {/* TEAM TAB */}
                  <TabsContent value="team" className="p-4">
                    <ScrollArea className="h-[480px] pr-4">
                      <div className="space-y-8">
                        <Card className="glass-card shadow-lg border-white/50 dark:border-slate-700/50 overflow-hidden">
                          <div className="relative p-4 flex items-center gap-3 overflow-hidden">
                            <div className="absolute inset-0 bg-logo-gradient opacity-10" />
                            <Users className="w-5 h-5 text-slate-800 dark:text-slate-200 relative z-10" />
                            <h3 className="font-bold text-slate-800 dark:text-slate-200 relative z-10" style={headingFont}>Founders & Team</h3>
                          </div>
                          <CardContent className="p-6">
                            <div className="grid gap-6 md:grid-cols-2">
                              {founders.map((founder, index) => (
                                <Card key={index} className="rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-all overflow-hidden bg-white/40 dark:bg-slate-900/40">
                                  <div className="p-5 flex items-center gap-4 border-b border-slate-100 dark:border-slate-800 bg-white/30 dark:bg-slate-900/30">
                                    <div className="w-14 h-14 bg-logo-gradient rounded-2xl flex items-center justify-center shrink-0 shadow-lg">
                                      <span className="material-symbols-outlined text-white text-2xl">person</span>
                                    </div>
                                    <div className="min-w-0">
                                      <h4 className="font-black text-slate-900 dark:text-white truncate" style={headingFont}>{founder.name}</h4>
                                      <p className="text-[10px] font-black text-[#A378CF] uppercase tracking-wider truncate">{founder.role}</p>
                                    </div>
                                  </div>
                                  <CardContent className="p-5 space-y-4">
                                    {founder.linkedin_url && (
                                      <a href={founder.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs font-bold text-[#3B52D0] hover:underline">
                                        <span className="material-symbols-outlined text-sm">link</span>
                                        LinkedIn Profile
                                      </a>
                                    )}

                                    {founder.bio && (
                                      <div>
                                        <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Bio</h5>
                                        <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed line-clamp-4">{founder.bio}</p>
                                      </div>
                                    )}

                                    <div className="grid grid-cols-2 gap-3 pt-2">
                                      {founder.equity_split && (
                                        <div className="bg-white/50 dark:bg-slate-800/50 p-2 rounded-xl border border-slate-100 dark:border-slate-700">
                                          <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Equity</p>
                                          <p className="text-[10px] font-bold text-slate-900 dark:text-white">{founder.equity_split}%</p>
                                        </div>
                                      )}
                                      {founder.domain_expertise_years && (
                                        <div className="bg-white/50 dark:bg-slate-800/50 p-2 rounded-xl border border-slate-100 dark:border-slate-700">
                                          <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Expertise</p>
                                          <p className="text-[10px] font-bold text-slate-900 dark:text-white">{founder.domain_expertise_years} Years</p>
                                        </div>
                                      )}
                                      {founder.previous_exits && (
                                        <div className="bg-white/50 dark:bg-slate-800/50 p-2 rounded-xl border border-slate-100 dark:border-slate-700">
                                          <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Exits</p>
                                          <p className="text-[10px] font-bold text-slate-900 dark:text-white truncate">{founder.previous_exits}</p>
                                        </div>
                                      )}
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>

                            {metadata?.advisory_board && metadata.advisory_board.length > 0 && (
                              <div className="mt-10">
                                <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest mb-6 px-2">Advisory Board</h3>
                                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                  {metadata.advisory_board.map((advisor, i) => (
                                    <div key={i} className="p-4 rounded-2xl bg-white/30 dark:bg-slate-900/30 border border-slate-200 dark:border-slate-800 shadow-sm">
                                      <h4 className="font-bold text-slate-900 dark:text-white text-sm">{advisor.name}</h4>
                                      <p className="text-[10px] font-bold text-[#4194F2] uppercase mt-1">{advisor.expertise}</p>
                                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-2 line-clamp-2">{advisor.background}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="mt-10 grid gap-4 sm:grid-cols-2">
                              {metadata?.key_hires_planned && (
                                <div className="p-5 bg-[#4194F2]/5 dark:bg-[#4194F2]/20 rounded-2xl border border-[#4194F2]/20">
                                  <h3 className="text-[10px] font-black text-[#4194F2] uppercase tracking-widest mb-3">Key Hires Planned</h3>
                                  <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">{metadata.key_hires_planned}</p>
                                </div>
                              )}
                              {metadata?.team_gaps && (
                                <div className="p-5 bg-[#F0766E]/5 dark:bg-[#F0766E]/20 rounded-2xl border border-[#F0766E]/20">
                                  <h3 className="text-[10px] font-black text-[#F0766E] uppercase tracking-widest mb-3">Team Gaps</h3>
                                  <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">{metadata.team_gaps}</p>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </ScrollArea>
                  </TabsContent>

                  {/* LEGAL TAB */}
                  <TabsContent value="legal" className="p-4">
                    <ScrollArea className="h-[480px] pr-4">
                      <div className="space-y-6">
                        <Card className="glass-card shadow-lg border-white/50 dark:border-slate-700/50 overflow-hidden">
                          <div className="relative p-4 flex items-center gap-3 overflow-hidden">
                            <div className="absolute inset-0 bg-logo-gradient opacity-10" />
                            <Scale className="w-5 h-5 text-slate-800 dark:text-slate-200 relative z-10" />
                            <h3 className="font-bold text-slate-800 dark:text-slate-200 relative z-10" style={headingFont}>Legal & Compliance</h3>
                          </div>
                          <CardContent className="pt-6 space-y-6">
                            <div className="grid gap-4 md:grid-cols-2">
                              <InfoRow icon={Briefcase} label="Company Structure" value={company_info.company_structure ? capitalizeFirst(company_info.company_structure.replace('_', ' ')) : undefined} />
                              <InfoRow icon={MapPin} label="Incorporation Location" value={company_info.incorporation_location} />
                            </div>

                            {company_info.regulatory_requirements && (
                              <div className="p-5 bg-white/30 dark:bg-slate-900/30 rounded-2xl border border-slate-200/50 dark:border-slate-700/50">
                                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">Regulatory Requirements</h3>
                                <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">{company_info.regulatory_requirements}</p>
                              </div>
                            )}

                            {company_info.legal_issues && (
                              <div className="p-5 bg-[#F0766E]/5 dark:bg-[#F0766E]/20 rounded-2xl border-2 border-[#F0766E]/30 relative overflow-hidden group">
                                <div className="absolute -top-6 -right-6 w-20 h-20 bg-[#F0766E] opacity-10 rounded-full group-hover:scale-125 transition-transform"></div>
                                <h3 className="text-[10px] font-black text-[#F0766E] uppercase tracking-widest mb-3 flex items-center gap-2">
                                  <AlertTriangle className="w-4 h-4" />
                                  Legal Issues Identified
                                </h3>
                                <p className="text-slate-700 dark:text-[#fca5a5] text-sm font-bold leading-relaxed">{company_info.legal_issues}</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </ScrollArea>
                  </TabsContent>

                  {/* VISION TAB */}
                  <TabsContent value="vision" className="p-4">
                    <ScrollArea className="h-[480px] pr-4">
                      <div className="space-y-6">
                        <Card className="glass-card shadow-lg border-white/50 dark:border-slate-700/50 overflow-hidden">
                          <div className="relative p-4 flex items-center gap-3 overflow-hidden">
                            <div className="absolute inset-0 bg-logo-gradient opacity-10" />
                            <Rocket className="w-5 h-5 text-slate-800 dark:text-slate-200 relative z-10" />
                            <h3 className="font-bold text-slate-800 dark:text-slate-200 relative z-10" style={headingFont}>Vision & Strategy</h3>
                          </div>
                          <CardContent className="pt-6 space-y-6">
                            <div className="grid gap-6">
                              {company_info.mission_statement && (
                                <div className="p-6 bg-[#3B52D0]/5 dark:bg-[#3B52D0]/10 rounded-3xl border border-[#3B52D0]/20 relative overflow-hidden group">
                                  <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:scale-110 transition-transform">
                                    <span className="material-symbols-outlined text-6xl text-[#3B52D0]">emoji_objects</span>
                                  </div>
                                  <h3 className="text-[10px] font-black text-[#3B52D0] uppercase tracking-widest mb-3">Mission Statement</h3>
                                  <p className="text-slate-800 dark:text-slate-200 font-bold text-lg leading-relaxed relative z-10 italic">"{company_info.mission_statement}"</p>
                                </div>
                              )}

                              <div className="grid gap-6 sm:grid-cols-2">
                                {company_info.five_year_vision && (
                                  <div className="p-5 bg-white/30 dark:bg-slate-900/30 rounded-2xl border border-slate-200 dark:border-slate-700">
                                    <h3 className="text-[10px] font-black text-[#4194F2] uppercase tracking-widest mb-3">5-Year Vision</h3>
                                    <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">{company_info.five_year_vision}</p>
                                  </div>
                                )}
                                {company_info.exit_strategy && (
                                  <div className="p-5 bg-white/30 dark:bg-slate-900/30 rounded-2xl border border-slate-200 dark:border-slate-700">
                                    <h3 className="text-[10px] font-black text-[#A378CF] uppercase tracking-widest mb-3">Exit Strategy</h3>
                                    <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">{company_info.exit_strategy}</p>
                                  </div>
                                )}
                              </div>

                              {company_info.social_impact && (
                                <div className="p-5 bg-[#10b981]/5 dark:bg-[#10b981]/10 rounded-2xl border border-[#10b981]/20">
                                  <h3 className="text-[10px] font-black text-[#10b981] uppercase tracking-widest mb-3">Social Impact</h3>
                                  <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">{company_info.social_impact}</p>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </ScrollArea>
                  </TabsContent>

                  {/* DOCUMENTS TAB */}
                  <TabsContent value="documents" className="p-4">
                    <ScrollArea className="h-[480px] pr-4">
                      <div className="space-y-6">
                        <Card className="glass-card shadow-lg border-white/50 dark:border-slate-700/50 overflow-hidden">
                          <div className="relative p-4 flex items-center gap-3 overflow-hidden">
                            <div className="absolute inset-0 bg-logo-gradient opacity-10" />
                            <FileText className="w-5 h-5 text-slate-800 dark:text-slate-200 relative z-10" />
                            <h3 className="font-bold text-slate-800 dark:text-slate-200 relative z-10" style={headingFont}>Uploaded Documents</h3>
                          </div>
                          <CardContent className="p-6">
                            {documents.length === 0 ? (
                              <div className="text-center py-12 text-slate-400 bg-white/20 dark:bg-slate-900/20 rounded-3xl border border-dashed border-slate-200 dark:border-slate-800">
                                <span className="material-symbols-outlined text-6xl mb-4 opacity-20">inventory_2</span>
                                <p className="font-bold">No documents uploaded yet</p>
                              </div>
                            ) : (
                              <div className="grid gap-4">
                                {documents.map((doc, index) => {
                                  const isPdf = doc.filename?.toLowerCase().endsWith('.pdf')
                                  const canPreview = isPdf && doc.storage_path

                                  return (
                                    <div
                                      key={doc.id || index}
                                      className={`flex items-center justify-between p-4 rounded-2xl bg-white/50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 hover:shadow-md transition-all group ${canPreview ? 'cursor-pointer hover:border-amber-200 dark:hover:border-amber-800' : ''}`}
                                      onClick={canPreview ? () => {
                                        setPreviewGcsPath(doc.storage_path)
                                        setPreviewFilename(doc.filename)
                                        setPreviewOpen(true)
                                      } : undefined}
                                    >
                                      <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                                          <FileText className="w-6 h-6 text-[#3B52D0]" />
                                        </div>
                                        <div className="min-w-0">
                                          <p className="font-bold text-slate-900 dark:text-white truncate max-w-[150px] sm:max-w-xs">{doc.filename}</p>
                                          <p className="text-[10px] font-black text-[#A378CF] uppercase tracking-widest mt-0.5">
                                            {doc.document_type ? doc.document_type.replace('_', ' ') : 'Document'}
                                            {doc.file_size && ` • ${formatFileSize(doc.file_size)}`}
                                            {canPreview && <span className="text-amber-500 ml-1">• Click to preview</span>}
                                          </p>
                                        </div>
                                      </div>
                                      {doc.id && doc.storage_path && (
                                        <div className="flex items-center gap-1">
                                          {canPreview && (
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="rounded-full hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-all"
                                              title="Preview document"
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                setPreviewGcsPath(doc.storage_path)
                                                setPreviewFilename(doc.filename)
                                                setPreviewOpen(true)
                                              }}
                                            >
                                              <Eye className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                                            </Button>
                                          )}
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            asChild
                                            className="rounded-full hover:bg-logo-gradient hover:text-white transition-all shadow-sm"
                                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                          >
                                            <a href={startupsApi.downloadDocument(startup.id, doc.id)} download>
                                              <Download className="w-5 h-5" />
                                            </a>
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </ScrollArea>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          <div className="w-full lg:w-96 lg:sticky lg:top-4">
            <ActivityFeed startupId={id!} />
          </div>
        </div>

        {isFounder && (
          <div className="space-y-6 mt-8">
            {pendingQuestions.length > 0 && (
              <Card className="border-none shadow-lg glass-card overflow-hidden">
                <div className="relative p-4 flex items-center justify-between overflow-hidden">
                  <div className="absolute inset-0 bg-logo-gradient opacity-10" />
                  <div className="flex items-center gap-3 relative z-10">
                    <MessageSquare className="w-5 h-5 text-slate-800 dark:text-slate-200" />
                    <div>
                      <h3 className="text-slate-800 dark:text-slate-200 font-bold" style={headingFont}>Open Questions ({pendingQuestions.length})</h3>
                      <p className="text-slate-500 dark:text-slate-400 text-[10px] uppercase font-black tracking-widest">Awaiting founder response</p>
                    </div>
                  </div>
                  <AnswerQuestionsModal
                    questions={pendingQuestions}
                    startupId={id!}
                  />
                </div>
                <CardContent className="p-6">
                  <QuestionsList
                    questions={pendingQuestions}
                    emptyMessage="No pending questions"
                  />
                </CardContent>
              </Card>
            )}

            {answeredQuestions.length > 0 && (
              <Card className="border-none shadow-lg glass-card overflow-hidden">
                <div className="relative p-4 flex items-center gap-3 overflow-hidden">
                  <div className="absolute inset-0 bg-logo-gradient opacity-10" />
                  <CheckCircle className="w-5 h-5 text-slate-800 dark:text-slate-200 relative z-10" />
                  <div className="relative z-10">
                    <h3 className="text-slate-800 dark:text-slate-200 font-bold" style={headingFont}>Answered Questions ({answeredQuestions.length})</h3>
                    <p className="text-slate-500 dark:text-slate-400 text-[10px] uppercase font-black tracking-widest">Publicly visible to investors</p>
                  </div>
                </div>
                <CardContent className="p-6">
                  <QuestionsList
                    questions={answeredQuestions}
                    emptyMessage="No answered questions yet"
                  />
                </CardContent>
              </Card>
            )}

            {allQuestions.length === 0 && (
              <Card className="glass-card border-none shadow-lg">
                <CardContent className="py-12 text-center">
                  <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageSquare className="h-8 w-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white" style={headingFont}>No questions yet</h3>
                  <p className="text-slate-500 dark:text-slate-400">Questions from investors will appear here</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {!isFounder && (
          <Card className="mt-8 border-none shadow-lg glass-card overflow-hidden">
            <div className="relative p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 overflow-hidden">
              <div className="absolute inset-0 bg-logo-gradient opacity-10" />
              <div className="flex items-center gap-3 relative z-10 w-full sm:w-auto">
                <MessageSquare className="w-5 h-5 text-slate-800 dark:text-slate-200" />
                <div className="flex-1">
                  <h3 className="text-slate-800 dark:text-slate-200 font-bold" style={headingFont}>Questions & Answers ({allQuestions.length})</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-[10px] uppercase font-black tracking-widest">Interaction with founders</p>
                </div>
              </div>

              <div className="flex items-center gap-2 relative z-10 w-full sm:w-auto">
                {/* Sort & Filter Buttons */}
                <div className="flex items-center bg-white/50 dark:bg-slate-800/50 rounded-xl p-1 border border-slate-200/50 dark:border-slate-700/50 mr-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "h-8 px-3 text-[10px] font-black uppercase tracking-wider rounded-lg",
                      questionFilter === 'all' ? "bg-white dark:bg-slate-700 shadow-sm text-[#3B52D0]" : "text-slate-500"
                    )}
                    onClick={() => setQuestionFilter('all')}
                  >
                    All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "h-8 px-3 text-[10px] font-black uppercase tracking-wider rounded-lg",
                      questionFilter === 'answered' ? "bg-white dark:bg-slate-700 shadow-sm text-[#10b981]" : "text-slate-500"
                    )}
                    onClick={() => setQuestionFilter('answered')}
                  >
                    Answered
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "h-8 px-3 text-[10px] font-black uppercase tracking-wider rounded-lg",
                      questionFilter === 'pending' ? "bg-white dark:bg-slate-700 shadow-sm text-[#f59e0b]" : "text-slate-500"
                    )}
                    onClick={() => setQuestionFilter('pending')}
                  >
                    Pending
                  </Button>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="h-10 px-4 rounded-xl border-slate-200 dark:border-slate-700 font-bold bg-white/50 dark:bg-slate-800/50"
                  onClick={() => setQuestionSort(prev => prev === 'newest' ? 'oldest' : 'newest')}
                >
                  <span className="material-symbols-outlined text-sm mr-2">{questionSort === 'newest' ? 'vertical_align_bottom' : 'vertical_align_top'}</span>
                  {questionSort === 'newest' ? 'Newest' : 'Oldest'}
                </Button>

                <AskQuestionModal startupId={id!} />
              </div>
            </div>
            {allQuestions.length > 0 ? (
              <CardContent className="p-6">
                <ScrollArea className="h-[650px] pr-4">
                  <QuestionsList questions={filteredQuestions} />
                </ScrollArea>
              </CardContent>
            ) : (
              <CardContent className="py-16 text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 dark:bg-slate-800/50 rounded-full mb-4 border border-white/30">
                  <MessageSquare className="h-10 w-10 text-[#4194F2]" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2" style={headingFont}>No questions asked yet</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">Be the first to ask a question about this startup</p>
              </CardContent>
            )}
          </Card>
        )}
      </div>

      {/* Document Preview Modal */}
      <DocumentPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        gcsPath={previewGcsPath}
        startupId={id!}
        filename={previewFilename}
      />
    </div >
  )
}
